import React from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowRightOutlined,
  BarChartOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  SyncOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";

type Feature = {
  label: string;
  icon: React.ReactNode;
};

type Stat = {
  label: string;
  value: string;
  icon: React.ReactNode;
  iconClass: string;
};

const features: Feature[] = [
  {
    label: "YouTube video management",
    icon: <YoutubeOutlined />,
  },
  {
    label: "Video scheduling",
    icon: <CalendarOutlined />,
  },
  {
    label: "Live-stream management",
    icon: <PlayCircleOutlined />,
  },
  {
    label: "Streaming reports & analytics",
    icon: <BarChartOutlined />,
  },
];

const stats: Stat[] = [
  {
    label: "Total Schedules",
    value: "201",
    icon: <CalendarOutlined />,
    iconClass: "bg-[#EEF0FF] text-[#1D1F5D]",
  },
  {
    label: "Active Live",
    value: "10",
    icon: <PlayCircleOutlined />,
    iconClass: "bg-[#E8F9F2] text-[#00B86B]",
  },
  {
    label: "Rendering",
    value: "0",
    icon: <SyncOutlined />,
    iconClass: "bg-[#FFF5E5] text-[#E99A00]",
  },
  {
    label: "Rendered",
    value: "67",
    icon: <CheckCircleOutlined />,
    iconClass: "bg-[#EAF6FF] text-[#1597D4]",
  },
  {
    label: "In Queue",
    value: "0",
    icon: <SyncOutlined />,
    iconClass: "bg-[#FFF1E8] text-[#F07828]",
  },
  {
    label: "Failed",
    value: "0",
    icon: <CheckCircleOutlined />,
    iconClass: "bg-[#FFECEC] text-[#E5484D]",
  },
];

const Hero = () => {
  const isLoggedIn = Boolean(localStorage.getItem("AUTH_TOKEN"));

  const actions = isLoggedIn
    ? [
        {
          label: "Dashboard",
          to: "/dashboard",
          icon: <ArrowRightOutlined />,
          className: "bg-[#00B86B] text-white hover:bg-[#00995A]",
        },
        {
          label: "Reports",
          to: "/report",
          icon: <BarChartOutlined />,
          className:
            "border border-[#1D1F5D]/15 bg-white text-[#1D1F5D] hover:border-[#1D1F5D]/30 hover:bg-[#F7F8FC]",
        },
      ]
    : [
        {
          label: "Login to KGS Studio",
          to: "/login",
          icon: <ArrowRightOutlined />,
          className: "bg-[#00B86B] text-white hover:bg-[#00995A]",
        },
        {
          label: "Read Privacy Policy",
          to: "/privacy-policy",
          icon: <ArrowRightOutlined />,
          className:
            "border border-[#1D1F5D]/15 bg-white text-[#1D1F5D] hover:border-[#1D1F5D]/30 hover:bg-[#F7F8FC]",
        },
      ];

  return (
    <section className="relative overflow-hidden bg-[#F7F8FC]">
      <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-[#1D1F5D]/[0.04] blur-3xl" />

      <div className="pointer-events-none absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-[#00B86B]/[0.05] blur-3xl" />

      <div className="relative mx-auto max-w-[1440px] px-5 py-14 sm:px-8 sm:py-20 lg:px-10 lg:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
          <div className="max-w-2xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#1D1F5D]/10 bg-white px-3.5 py-1.5 text-xs font-semibold text-[#1D1F5D] shadow-sm">
            KGS Studio by Khan Global Studies
            </div>
            <h1 className="text-4xl font-bold leading-[1.08] tracking-tight text-[#1D1F5D] sm:text-5xl lg:text-5xl">
              KGS Studio
            </h1>
            <h2 className="mt-3 max-w-2xl text-2xl font-semibold leading-tight text-[#172033] sm:text-3xl lg:text-[38px]">
            Manage YouTube videos and live broadcasts from one place.
            </h2>
            <p className="mt-6 max-w-xl text-base leading-6 text-[#64748B] sm:text-md">
            KGS Studio is a video management and live-streaming platform by Khan Global Studies. It allows authorized users to manage YouTube videos, schedule content, and manage live broadcasts through Google's YouTube APIs.
            </p>
            <div className="mt-8 grid max-w-xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              {features.map((feature) => (
                <div
                  key={feature.label}
                  className="flex items-center gap-3 text-sm font-medium text-[#334155]"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#1D1F5D]/[0.07] text-[#1D1F5D]">
                    {feature.icon}
                  </span>

                  {feature.label}
                </div>
              ))}
            </div>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              {actions.map((action) => (
                <Link
                  key={action.label}
                  to={action.to}
                  className={`inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3.5 text-sm font-semibold no-underline shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${action.className}`}
                >
                  {action.label}
                </Link>
              ))}
            </div>

            <p className="mt-4 text-xs text-[#94A3B8]">
              {isLoggedIn
                ? "Access your KGS Studio dashboard and reporting tools."
                : "Sign in is required to use Studio tools."}
            </p>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-[#1D1F5D]/[0.04] blur-2xl" />

            <div className="relative overflow-hidden rounded-2xl border border-[#E2E5ED] bg-white shadow-[0_20px_60px_rgba(29,31,93,0.10)]">
              <div className="border-b border-[#E8EAF0] px-5 py-5 sm:px-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#1D1F5D]/[0.07] text-[#1D1F5D]">
                        <BarChartOutlined />
                      </div>

                      <div>
                        <h3 className="m-0 text-sm font-bold text-[#172033]">
                          Dashboard Overview
                        </h3>

                        <p className="m-0 mt-0.5 text-xs text-[#64748B]">
                          Monitor your video streaming metrics
                        </p>
                      </div>
                    </div>
                  </div>

                  <span className="hidden rounded-full bg-[#E8F9F2] px-3 py-1 text-[10px] font-semibold text-[#008F59] sm:block">
                    KGS Studio
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-3 sm:p-6">
                {stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-xl border border-[#E8EAF0] bg-white p-3 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#1D1F5D]/15 hover:shadow-sm sm:p-4"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-base ${stat.iconClass}`}
                      >
                        {stat.icon}
                      </div>

                      <div className="min-w-0">
                        <p className="m-0 truncate text-[10px] font-medium text-[#64748B] sm:text-[11px]">
                          {stat.label}
                        </p>

                        <p className="m-0 mt-1 text-lg font-bold text-[#172033] sm:text-xl">
                          {stat.value}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between border-t border-[#E8EAF0] bg-[#FAFBFC] px-5 py-3 sm:px-6">
                <p className="m-0 text-[11px] text-[#64748B]">
                  KGS Studio dashboard preview
                </p>

                <div className="flex items-center gap-1.5 text-[10px] font-medium text-[#00A862]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00B86B]" />
                  System ready
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
